import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { NAVIGATION_KEYS, NS } from '../../i18n/constants/keys';
import PermissionGate from '../../utils/hooks/PermissionGate';
import { ChevronDownIcon, ChevronRightIcon } from '../../icons/animated';
import {
  PrivateSidebarItems,
  PublicSidebarItems,
} from '../layout/Sidebar/MenuItems';
import { MobileNotifications } from './MobileNotifications';
import { MobileProfile } from './MobileProfile';
import styles from './NavBar.module.css';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  openDropdown: string | null;
  setOpenDropdown: (label: string | null) => void;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose,
  openDropdown,
  setOpenDropdown,
}) => {
  const { t } = useTranslation(NS.navigation);
  const location = useLocation();

  const handleDropdownToggle = (labelKey: string) => {
    setOpenDropdown(openDropdown === labelKey ? null : labelKey);
  };

  const handleLinkClick = () => {
    onClose();
  };

  if (!isOpen) return null;

  const items = [...PrivateSidebarItems, ...PublicSidebarItems];

  return (
    <>
      <div
          className={styles.mobileMenuOverlay}
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 64,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
          }}
      />

      <div
          className={`${styles.mobileMenu} ${isOpen ? styles.open : ''}`}
          style={{
            position: 'fixed',
            top: 64,
            right: 0,
            bottom: 0,
            width: '380px',
            maxWidth: '100vw',
            zIndex: 1001,
          }}
      >
        <div className={`${styles.mobileMenuContent} p-3`}>
          <div className='rounded-xl border border-slate-200 bg-white p-3 shadow-sm'>
            <h3 className='mb-2 px-1 text-[11px] font-extrabold uppercase tracking-wide text-slate-400'>
              {t(NAVIGATION_KEYS.mobileMenu.navigation)}
            </h3>
            <div className='space-y-1'>
              { items.map((item) => {
                const hasSubItems = Boolean(item.subItems?.length);
                const isActive =
                  item.to && item.to !== '' && location.pathname === item.to;
                const isDropdownOpen = openDropdown === item.labelKey;
                const itemLabel = t(item.labelKey);

                return (
                  <PermissionGate
                      key={item.labelKey}
                      requiredPermissions={item.assignedPermissions}
                      matchAnyPermission={item.matchAnyPermission}
                  >
                    <div>
                      { hasSubItems ? (
                        <button
                            onClick={() => handleDropdownToggle(item.labelKey)}
                            className={`flex min-h-[42px] w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition ${
                              isDropdownOpen
                                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                                : 'text-slate-700 hover:bg-slate-50'
                            }`}
                        >
                          <div className='flex min-w-0 items-center gap-3'>
                            { React.cloneElement(item.icon, {
                              className: `h-[18px] w-[18px] flex-shrink-0 ${
                                isDropdownOpen
                                  ? 'text-emerald-600'
                                  : 'text-slate-500'
                              }`,
                            }) }
                            <span className='truncate font-bold'>{ itemLabel }</span>
                          </div>
                          { isDropdownOpen ? (
                            <ChevronDownIcon size={16} className='flex-shrink-0' />
                          ) : (
                            <ChevronRightIcon size={16} className='flex-shrink-0' />
                          ) }
                        </button>
                      ) : (
                        <Link
                            to={item.to}
                            onClick={handleLinkClick}
                            className={`flex min-h-[42px] items-center gap-3 rounded-lg px-3 py-2 text-sm transition ${
                              isActive
                                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                                : 'text-slate-700 hover:bg-slate-50'
                            }`}
                        >
                          { React.cloneElement(item.icon, {
                            className: `h-[18px] w-[18px] flex-shrink-0 ${
                              isActive ? 'text-emerald-600' : 'text-slate-500'
                            }`,
                          }) }
                          <span className='truncate font-bold'>{ itemLabel }</span>
                        </Link>
                      ) }

                      { hasSubItems && isDropdownOpen && (
                        <div className='ml-6 mt-1 space-y-1 border-l border-emerald-100 pl-3'>
                          { item.subItems!.map((subItem) => {
                            const isSubActive =
                              location.pathname === subItem.to;
                            const subItemContent = (
                              <Link
                                  to={subItem.to || '#'}
                                  onClick={handleLinkClick}
                                  className={`block rounded-md px-3 py-2 text-[13px] font-semibold transition ${
                                    isSubActive
                                      ? 'bg-emerald-50 text-emerald-700'
                                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                                  }`}
                              >
                                { subItem.labelKey ? t(subItem.labelKey) : null }
                              </Link>
                            );

                            return subItem.permissionGate ? (
                              <PermissionGate
                                  key={subItem.labelKey}
                                  requiredPermissions={
                                    subItem.permissionGate.requiredPermissions
                                  }
                                  matchAnyPermission={
                                    subItem.permissionGate.matchAnyPermission
                                  }
                              >
                                { subItemContent }
                              </PermissionGate>
                            ) : (
                              <div key={subItem.labelKey}>{ subItemContent }</div>
                            );
                          }) }
                        </div>
                      ) }
                    </div>
                  </PermissionGate>
                );
              }) }
            </div>
          </div>

          <div className='mt-3'>
            <MobileProfile />
          </div>

          <div className='mt-3'>
            <MobileNotifications />
          </div>
        </div>
      </div>
    </>
  );
};
